
/**
 * Calendar integration utilities
 */

export interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  startTime: string;  // ISO format
  endTime: string;    // ISO format
}

export const calendar = {
  // Generate Google Calendar link
  generateGoogleCalendarLink: (event: CalendarEvent): string => {
    const baseUrl = 'https://calendar.google.com/calendar/render';
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates: `${formatDateForGoogle(event.startTime)}/${formatDateForGoogle(event.endTime)}`,
      details: event.description || '',
      location: event.location || ''
    });
    return `${baseUrl}?${params.toString()}`;
  },

  // Generate WhatsApp reminder message
  generateWhatsAppMessage: (event: CalendarEvent): string => {
    const date = new Date(event.startTime);
    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });

    return encodeURIComponent(
      `📅 Event Reminder:\n\n` +
      `${event.title}\n` +
      `Date: ${dateStr}\n` +
      `Time: ${timeStr}\n` +
      `${event.location ? `Location: ${event.location}\n` : ''}` +
      `${event.description ? `\n${event.description}` : ''}`
    );
  },

  // Generate iCal file content
  generateICalFile: (event: CalendarEvent): string => {
    const icalContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `DTSTART:${formatDateForICal(event.startTime)}`,
      `DTEND:${formatDateForICal(event.endTime)}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description || ''}`,
      `LOCATION:${event.location || ''}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    return icalContent;
  },

  // Download iCal file
  downloadICalFile: (event: CalendarEvent, filename: string = 'event.ics') => {
    const content = calendar.generateICalFile(event);
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Helper functions
function formatDateForGoogle(isoDate: string): string {
  return new Date(isoDate).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

function formatDateForICal(isoDate: string): string {
  return new Date(isoDate).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}
